import { computed, shallowRef, toRaw, watch, type Ref } from 'vue';
import { onDispose } from './element';

/** One recorded state and when it was recorded. */
export interface NxUndoRecord<T> {
  value: T;
  at: number;
}

export interface NxUndoHistoryOptions<T> {
  /** Most undo steps kept; older ones drop off. Default 100. */
  capacity?: number;
  /** Copies a state so later edits don't reach the history. Default: structured clone. */
  clone?: (value: T) => T;
  /** Commits this close to the previous one fold into it (typing, dragging). Default 0. */
  mergeWithinMs?: number;
  /** Commit on every change of `source` (deep) instead of calling `commit()`. Default `false`. */
  auto?: boolean;
  /** Clock, for tests. */
  now?: () => number;
}

function defaultClone<T>(value: T): T {
  const raw = toRaw(value);
  if (raw === null || typeof raw !== 'object') return raw;
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(raw);
    } catch {
      // Functions or class instances inside: fall through to JSON.
    }
  }
  return JSON.parse(JSON.stringify(raw)) as T;
}

/**
 * Undo and redo for a piece of editor state. The history holds snapshots:
 * call `commit()` after each change (or pass `auto: true`), and `undo()` /
 * `redo()` write the neighbouring snapshot back into `source`. A commit after
 * an undo discards the redo branch, as in every editor. `mergeWithinMs`
 * folds rapid commits (keystrokes, drag frames) into one step.
 *
 * `history` lists the states you can go back to (oldest first), `future` the
 * ones redo brings back (next first).
 */
export function useUndoHistory<T>(source: Ref<T>, options: NxUndoHistoryOptions<T> = {}) {
  const capacity = Math.max(1, options.capacity ?? 100);
  const clone = options.clone ?? defaultClone;
  const now = options.now ?? Date.now;

  const past = shallowRef<NxUndoRecord<T>[]>([]);
  const future = shallowRef<NxUndoRecord<T>[]>([]);
  const present = shallowRef<NxUndoRecord<T>>({ value: clone(source.value), at: now() });
  let applying = false;
  // Start of the step commits may still fold into; none after undo/redo/clear.
  let stepStart: number | null = null;

  function commit(): void {
    const at = now();
    const record = { value: clone(source.value), at };
    const mergeMs = options.mergeWithinMs ?? 0;
    if (mergeMs > 0 && stepStart !== null && at - stepStart < mergeMs) {
      // Measured from the step's start, so a steady stream of edits still ends one day.
      present.value = record;
    } else {
      past.value = [...past.value, present.value].slice(-capacity);
      present.value = record;
      stepStart = at;
    }
    if (future.value.length) future.value = [];
  }

  function apply(record: NxUndoRecord<T>) {
    stepStart = null;
    applying = true;
    try {
      source.value = clone(record.value);
    } finally {
      applying = false;
    }
  }

  function undo(): boolean {
    const previous = past.value[past.value.length - 1];
    if (!previous) return false;
    past.value = past.value.slice(0, -1);
    future.value = [present.value, ...future.value];
    present.value = previous;
    apply(previous);
    return true;
  }

  function redo(): boolean {
    const next = future.value[0];
    if (!next) return false;
    future.value = future.value.slice(1);
    past.value = [...past.value, present.value].slice(-capacity);
    present.value = next;
    apply(next);
    return true;
  }

  /** Forget every step; the current value becomes the new starting point. */
  function clear(): void {
    past.value = [];
    future.value = [];
    present.value = { value: clone(source.value), at: now() };
    stepStart = null;
  }

  if (options.auto) {
    const stop = watch(
      source,
      () => {
        if (!applying) commit();
      },
      { deep: true, flush: 'sync' }
    );
    onDispose(stop);
  }

  return {
    commit,
    undo,
    redo,
    clear,
    canUndo: computed(() => past.value.length > 0),
    canRedo: computed(() => future.value.length > 0),
    history: computed<readonly NxUndoRecord<T>[]>(() => past.value),
    future: computed<readonly NxUndoRecord<T>[]>(() => future.value),
  };
}
